/**
 * `<ErrorBanner>` — single shared error surface for every form / page in
 * the SPA (issue: "Surface request ID in the SPA's error UI").
 *
 * Accepts the live `error` value the route is holding so callers don't
 * need to flatten to `err.message` (the act that was discarding the
 * request id). Rendering rules:
 *   - `null` → renders nothing; callers can drop conditional wrapping.
 *   - `string` → message only, no reference row.
 *   - `Error` (non-`ApiError`, e.g. network failure) → message only.
 *   - `ApiError` → message + a "Reference: <requestId>" row with a Copy
 *     button so the user can paste the id straight into a support email.
 *
 * The Copy button mirrors the existing `TotpSecretDisplay` pattern in
 * `routes/admin/user-detail.tsx` — `navigator.clipboard.writeText`,
 * label flips to "Copied!" for 2s, falls back silently when the
 * Clipboard API is blocked (the id stays on-screen and selectable).
 */

import { useState } from 'react';
import { ApiError } from '../api/client.js';

interface Props {
  error: ApiError | Error | string | null;
  /** Optional message override. When supplied, this string is shown in
   *  place of `error.message` — useful when the route maps a known
   *  `error.code` to friendlier copy but still wants the reference row
   *  to surface the underlying request id. */
  message?: string;
}

export function ErrorBanner({ error, message }: Props): JSX.Element | null {
  const [copied, setCopied] = useState(false);

  if (error == null) return null;

  const displayMessage = message ?? (typeof error === 'string' ? error : error.message);
  const requestId = error instanceof ApiError ? error.requestId : null;

  async function handleCopy(): Promise<void> {
    if (requestId == null) return;
    try {
      await navigator.clipboard.writeText(requestId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may be blocked (non-HTTPS, browser policy). The id
      // is still visible on screen so the user can select it manually.
    }
  }

  return (
    <div className="error-banner" role="alert" aria-live="assertive">
      <p className="error-banner-message">{displayMessage}</p>
      {requestId !== null ? (
        <p className="error-banner-reference">
          <span>
            Reference: <code>{requestId}</code>
          </span>
          <button type="button" className="error-banner-copy" onClick={() => void handleCopy()}>
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </p>
      ) : null}
    </div>
  );
}
